import {
  ApplicationDataTabType,
  useApplicationDataStore,
} from '../../../store/applicationDataStore';
import { ApplicationStatus, StatusGroup } from '@axo/shared/data-access/types';
import { Button } from '@blueprintjs/core';
import {
  ace_hooks,
  lock_hooks,
  real_estate_hooks,
  useApplicationStatusUtils,
  useLoanApplication,
  useUpdateApplicationDetails,
} from '@axo/shared/data-access/hooks';
import { useFormContext } from 'react-hook-form';
import { ConditionalTooltip } from '../../ConditionalTooltip/ConditionalTooltip';
import { Product } from '@axo/shared/data-access/types/loan_quote';
import { useCollateralFormStore } from '../../../forms/collateral/store';
import { useConfirmDialog } from '../../../hooks/utils/useConfirmDialog';
import { ApplicationConditionalWrapper } from '../../ConditionalWrappers/ApplicationConditionalWrapper';
import { MarketCountryCode } from '@axo/shared/types';
import { RoleName } from '@axo/shared/data-access/types/permissions';

interface ApplicationDataCardHeaderActionsProps {
  applicationID: string;
}

export const ApplicationDataCardHeaderActions: React.FC<
  ApplicationDataCardHeaderActionsProps
> = ({ applicationID }) => {
  const {
    isEditing,
    selectedTab,
    setIsEditing,
    createDraft,
    sendToBanks,
    selectAxoVersion,
  } = useApplicationDataStore(applicationID);
  const { data: applicationData } = useLoanApplication(applicationID);
  const {
    isInAnyApplicationStatusGroup,
    isFinalApplicationStatus,
    isEditableApplicationStatus,
  } = useApplicationStatusUtils();
  const { reset, formState } = useFormContext();
  const {
    mutateAsync: patchApplication,
    isLoading: isPatchApplicationLoading,
  } = useUpdateApplicationDetails();
  const { isEditing: isEditingCollateral } =
    useCollateralFormStore(applicationID);
  const { data: applicationCollateralData } =
    real_estate_hooks.useGetApplicationCollateral({
      applicationID,
    });

  const { confirm } = useConfirmDialog();

  const isSecuredLending = applicationData?.Product === Product.SecuredLoan;
  const { data: axoVersionData, isLoading: isLoadingAxoVersionData } =
    ace_hooks.useAxoVersion(isSecuredLending ? applicationID : undefined);

  const { mutateAsync: createAxoVersion, isLoading: isCreatingAxoVersion } =
    ace_hooks.useCreateAxoVersion();

  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    applicationData?.MarketCountry,
  ).data;

  if (isEditing) {
    return (
      <>
        <Button
          icon="cross"
          text="Discard"
          onClick={() => {
            reset();
            setIsEditing(false);
          }}
        />
        {[
          ApplicationDataTabType.ORIGINAL,
          ApplicationDataTabType.AXO_VERSION,
        ].includes(selectedTab.type) && (
          <ConditionalTooltip
            conditions={[
              {
                isTooltipVisible: !formState.isDirty,
                tooltipContent: 'Nothing to save',
                isChildDisabled: true,
              },
              {
                isTooltipVisible: !formState.isValid && !formState.isValidating,
                tooltipContent:
                  'One or more fields MIGHT have validation errors',
              },
            ]}
            render={(disabled) => (
              <Button
                disabled={disabled}
                loading={formState.isSubmitting}
                type="submit"
                icon={
                  applicationData?.Status ===
                  ApplicationStatus.ApplicationStatusValidationFailed
                    ? 'repeat'
                    : 'floppy-disk'
                }
                intent={
                  formState.isValid || formState.isValidating
                    ? 'primary'
                    : 'danger'
                }
                text={
                  applicationData?.Status ===
                  ApplicationStatus.ApplicationStatusValidationFailed
                    ? 'Save & Revalidate'
                    : 'Save'
                }
              />
            )}
          />
        )}
        {[ApplicationDataTabType.DRAFT].includes(selectedTab.type) && (
          <ConditionalTooltip
            conditions={[
              {
                isTooltipVisible: !formState.isValid && !formState.isValidating,
                tooltipContent:
                  'One or more fields MIGHT have validation errors',
              },
            ]}
            render={(disabled) => (
              <Button
                disabled={disabled}
                icon="send-to"
                text="Send to banks"
                intent={
                  formState.isValid || formState.isValidating
                    ? 'primary'
                    : 'danger'
                }
                onClick={() => sendToBanks()}
              />
            )}
          />
        )}
      </>
    );
  }

  return (
    <>
      <ConditionalTooltip
        conditions={[
          {
            isTooltipVisible: Boolean(
              applicationData?.Status &&
                isFinalApplicationStatus(applicationData.Status),
            ),
            tooltipContent: 'This application is in a final status',
            isChildDisabled: true,
          },
          {
            isTooltipVisible: Boolean(
              (isSecuredLending &&
                selectedTab.type === ApplicationDataTabType.ORIGINAL &&
                axoVersionData) ||
                isLoadingAxoVersionData,
            ),
            tooltipContent:
              "Can't edit original version if an axo version exists",
            isChildDisabled: true,
          },
          {
            isTooltipVisible:
              isSecuredLending &&
              selectedTab.type === ApplicationDataTabType.ORIGINAL &&
              !isInAnyApplicationStatusGroup(
                applicationData?.Status ?? undefined,
                [StatusGroup.Incomplete],
              ),
            tooltipContent:
              'Original version is completed and cannot be modified. Create an Axo version instead',
            isChildDisabled: true,
          },
          {
            isTooltipVisible:
              !isEditableApplicationStatus(
                applicationData?.Status ?? undefined,
              ) && selectedTab.type !== ApplicationDataTabType.AXO_VERSION,
            tooltipContent: 'This application is not an editable status',
            isChildDisabled: true,
          },
          {
            isTooltipVisible: ![
              ApplicationDataTabType.ORIGINAL,
              ApplicationDataTabType.AXO_VERSION,
            ].includes(selectedTab.type),
            tooltipContent: 'Can only edit original versions and axo versions',
            isChildDisabled: true,
          },
          {
            isTooltipVisible: !hasLock,
            tooltipContent: "You don't have the lock of this application",
            isChildDisabled: true,
          },
        ]}
        render={(disabled) => (
          <Button
            disabled={disabled}
            loading={isLoadingAxoVersionData}
            icon="edit"
            text="Edit"
            onClick={() => {
              setIsEditing(true);
            }}
          />
        )}
      />
      {isSecuredLending &&
        selectedTab.type === ApplicationDataTabType.ORIGINAL && (
          <ConditionalTooltip
            conditions={[
              {
                isTooltipVisible: !!axoVersionData,
                tooltipContent: 'An axo version already exists',
                isChildDisabled: true,
              },
              {
                isTooltipVisible: Boolean(
                  applicationData?.Status &&
                    isInAnyApplicationStatusGroup(applicationData.Status, [
                      StatusGroup.Incomplete,
                    ]),
                ),
                tooltipContent:
                  "Can't create an Axo version before customer completes original version",
                isChildDisabled: true,
              },
              {
                isTooltipVisible: Boolean(
                  applicationData?.Status &&
                    isFinalApplicationStatus(applicationData.Status),
                ),
                tooltipContent: 'This application is in a final status',
                isChildDisabled: true,
              },
              {
                isTooltipVisible: !hasLock,
                tooltipContent: "You don't have the lock of this application",
                isChildDisabled: true,
              },
            ]}
            render={(disabled: boolean) => (
              <Button
                loading={isCreatingAxoVersion}
                icon="annotation"
                text="Create Axo version"
                onClick={async () => {
                  if (!applicationData) return;

                  await createAxoVersion({
                    LoanApplicationID: applicationID,
                    OriginalRevision: applicationData.Revision ?? 0,
                  });

                  selectAxoVersion();
                }}
                intent="primary"
                disabled={disabled}
              />
            )}
          />
        )}
      {applicationData?.Status &&
        ![
          ApplicationStatus.ApplicationStatusCloned,
          ApplicationStatus.ApplicationStatusValidationFailed,
        ].includes(applicationData?.Status) &&
        isSecuredLending && (
          <ConditionalTooltip
            conditions={[
              {
                isTooltipVisible: Boolean(
                  (selectedTab.type === ApplicationDataTabType.ORIGINAL &&
                    axoVersionData) ||
                    isLoadingAxoVersionData,
                ),
                tooltipContent:
                  "Can't send to banks in original version when an Axo version exists",
                isChildDisabled: true,
              },
              {
                isTooltipVisible:
                  selectedTab.type === ApplicationDataTabType.REQUEST ||
                  selectedTab.type === ApplicationDataTabType.DRAFT,
                tooltipContent:
                  'Can only send to banks in original or Axo versions',
                isChildDisabled: true,
              },
              {
                isTooltipVisible: Boolean(
                  applicationData?.Status &&
                    isInAnyApplicationStatusGroup(applicationData.Status, [
                      StatusGroup.Incomplete,
                    ]),
                ),
                tooltipContent: 'This application is in an incomplete status',
                isChildDisabled: true,
              },
              {
                isTooltipVisible: Boolean(
                  applicationData?.Status &&
                    isFinalApplicationStatus(applicationData.Status),
                ),
                tooltipContent: 'This application is in a final status',
                isChildDisabled: true,
              },
              {
                isTooltipVisible: isEditingCollateral.size > 0,
                tooltipContent:
                  'Save your collateral changes before sending to bank',
                isChildDisabled: true,
              },
              {
                isTooltipVisible: !hasLock,
                tooltipContent: "You don't have the lock of this application",
                isChildDisabled: true,
              },
              {
                isTooltipVisible: !formState.isValid && !formState.isValidating,
                tooltipContent:
                  'One or more fields MIGHT have validation errors',
              },
            ]}
            render={(disabled: boolean) => (
              <Button
                icon="send-to"
                text="Send to banks"
                onClick={() => {
                  if (
                    isSecuredLending &&
                    applicationCollateralData?.length === 0
                  ) {
                    confirm({
                      cancelText: 'Cancel',
                      confirmText: 'Send to banks',
                      message:
                        'There are no collaterals added to this application. Are you sure you want to send to banks?',
                      confirmCallback: () => sendToBanks(),
                    });
                  } else {
                    sendToBanks();
                  }
                }}
                intent={
                  formState.isValid || formState.isValidating
                    ? 'primary'
                    : 'danger'
                }
                disabled={disabled}
              />
            )}
          />
        )}
      {applicationData?.Status ===
        ApplicationStatus.ApplicationStatusCloned && (
        <ApplicationConditionalWrapper
          blacklist={[
            {
              marketCountry: [MarketCountryCode.Finland],
              roles: [RoleName.Telemarketing, RoleName.TelemarketingLimited],
            },
          ]}
        >
          <Button
            loading={formState.isSubmitting || isPatchApplicationLoading}
            icon="tick"
            text="Complete cloning"
            intent={
              formState.isValid || formState.isValidating ? 'primary' : 'danger'
            }
            type="button"
            onClick={async () => {
              await patchApplication({
                applicationID,
                patch: {
                  Status:
                    ApplicationStatus.ApplicationStatusPendingReceiptCustomerInputComplete,
                },
              });
            }}
          />
        </ApplicationConditionalWrapper>
      )}
      {applicationData?.Status &&
        ![
          ApplicationStatus.ApplicationStatusCloned,
          ApplicationStatus.ApplicationStatusValidationFailed,
        ].includes(applicationData?.Status) &&
        !isSecuredLending && (
          <>
            <ConditionalTooltip
              conditions={[
                {
                  isTooltipVisible: Boolean(
                    applicationData?.Status &&
                      isInAnyApplicationStatusGroup(applicationData.Status, [
                        StatusGroup.Incomplete,
                      ]),
                  ),
                  tooltipContent: 'This application is in an incomplete status',
                  isChildDisabled: true,
                },
                {
                  isTooltipVisible: Boolean(
                    applicationData?.Status &&
                      isFinalApplicationStatus(applicationData.Status),
                  ),
                  tooltipContent: 'This application is in a final status',
                  isChildDisabled: true,
                },
                {
                  isTooltipVisible: !hasLock,
                  tooltipContent: "You don't have the lock of this application",
                  isChildDisabled: true,
                },
              ]}
              render={(disabled: boolean) => (
                <Button
                  icon="send-to-graph"
                  text="Create loan request"
                  onClick={() => createDraft()}
                  disabled={disabled}
                />
              )}
            />
          </>
        )}
      {applicationData?.Status ===
        ApplicationStatus.ApplicationStatusValidationFailed && (
        <ConditionalTooltip
          conditions={[
            {
              isTooltipVisible: !formState.isValid && !formState.isValidating,
              tooltipContent: 'One or more fields MIGHT have validation errors',
            },
          ]}
        >
          <Button
            loading={formState.isSubmitting}
            icon="repeat"
            text="Revalidate"
            intent={
              formState.isValid || formState.isValidating ? 'primary' : 'danger'
            }
            type="submit"
          />
        </ConditionalTooltip>
      )}
    </>
  );
};
